import { isStringNotBlank, isNotNullOrUndefined } from 'utils/utils';

const PARTNER_TYPE_SEGMENTS = ['private-individual', 'legal-entity', 'structure'] as const;
type PartnerTypeSegment = (typeof PARTNER_TYPE_SEGMENTS)[number];
export const DEFAULT_PARTNER_TYPE_SEGMENT: PartnerTypeSegment = 'private-individual';

export const getPartnerTypeSegment = (url: string = window.location.href): PartnerTypeSegment => {
  const segment = /\/partner\/([^/?#]+)/.exec(url)?.[1] as PartnerTypeSegment | undefined;
  return isNotNullOrUndefined(segment) && PARTNER_TYPE_SEGMENTS.includes(segment)
    ? segment
    : DEFAULT_PARTNER_TYPE_SEGMENT;
};

/**
 * Remove all empty (null, undefined, empty string) properties from an object (recursively, including nested objects).
 * Needed to send data that can be parsed by the backend.
 * @param obj The object to filter.
 */
export const removeEmptyProperties = (obj: any) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') {
      removeEmptyProperties(obj[key]); // recurse
    } else if ((obj[key] == null || obj[key] === '') && key !== 'validTo') {
      Reflect.deleteProperty(obj, key);
    }
  });
};

export const openInSameTab = (url: string) => {
  const newWindow = window.open(url, '_self');
  if (newWindow) {
    newWindow.opener = null;
  }
};

// When rendered inside a web component's shadow root (module federation host), the real
// content iframe lives in that shadow root, not in the host page's light DOM. Search there first
// so unrelated iframes injected into the host page (e.g. Gravity's tutorial widget) are never
// picked up; fall back to document for standalone mode (no shadow root).
export const getIFrame = (shadowRoot?: ShadowRoot) =>
  (shadowRoot ? shadowRoot.querySelector('iframe') : undefined) ?? document.getElementsByTagName('iframe')[0];

export const getPartnerCoreIFrame = () => document.getElementById('partner-iframe') as HTMLIFrameElement;

const srcUrl = import.meta.url;

export const isAbsoluteUrl = (urlString?: string) =>
  urlString?.indexOf('http://') === 0 || urlString?.indexOf('https://') === 0;

/**
 * If the application is loaded through a module federated component, the frontend controller url is returned.
 * Else an empty string is returned, since in those cases we want to load resources from the same origin.
 */
export const getHostBaseUrl = () => {
  const hostBaseUrl = isStringNotBlank(srcUrl) && isAbsoluteUrl(srcUrl) ? new URL(srcUrl).origin : '';
  const originUrl = window.location.origin;
  return hostBaseUrl === originUrl ? '' : hostBaseUrl;
};

export const getIconProviderUrl = () => `${new URL(srcUrl).origin}/assets/icons`;

/* Internal params for embedding the portal in the Wealth Navigator; not shown in its URL */
const EMBEDDING_SEARCH_PARAMS = ['mode', 'hideTopbar'];

const withoutEmbeddingParams = (search: string) => {
  const params = new URLSearchParams(search);
  EMBEDDING_SEARCH_PARAMS.forEach((param) => params.delete(param));
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * Builds the route the Wealth Navigator shows in its URL bar, query included.
 * No hash: the Wealth Navigator uses hash routing, so the whole route already sits after its '#'.
 */
export const toHostRoute = (path: string, search = '') => `${path}${withoutEmbeddingParams(search)}`;

/** Asks the Wealth Navigator to navigate to the given path. */
export const requestHostNavigation = (path: string, search = '') => {
  /*Inside an iframe, postMessage is the only way to reach the host*/
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'UPDATE_HOST_ROUTE', path, search: withoutEmbeddingParams(search) }, '*');
    return;
  }

  window.dispatchEvent(new CustomEvent('WN_NAVIGATE', { detail: toHostRoute(path, search) }));
};
